// Builds the AG Charts series that draw counterparty quotes over the swap curve (LQC-1899).
// Pure option objects - no JSX, no React, keep as .ts so the series shape is unit-testable
// without a canvas.
//
// One scatter series per counterparty, each dot on its real tenor. Positions are never nudged:
// where two counterparties quote the same tenor their dots genuinely overlap, and the surface ring
// plus the draw order below are what keep that readable.

import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import {
  CounterpartyQuoteMarker,
  groupQuotesByCounterparty,
  rankCounterpartyGroups,
} from "./counterpartyQuoteHelpers";
import {
  formatQuoteWithUnit,
  securityLabel,
  spreadYKey,
  SwapCurveYAxis,
  yAxisForNotation,
} from "./swapCurveHelpers";

// Marker shapes cycled per counterparty so overlapping dots stay distinguishable by shape as well
// as colour. "diamond" is deliberately absent - the indicative mid owns it. Shape is not
// decoration: it is the secondary encoding the palette's dark-mode CVD band obliges, and the only
// thing separating counterparties once the colour cap is reached.
export const CP_MARKER_SHAPES = [
  "circle",
  "square",
  "triangle",
  "cross",
  "plus",
  "heart",
];

// Marker size and the width of the surface-coloured ring around each one. The ring is what lifts a
// dot off the curve it is sitting on - without it a dot on the line reads as part of the line, and
// it is also what separates two counterparties' dots when they land on top of each other.
const CP_MARKER_SIZE = 9;
const CP_MARKER_RING_WIDTH = 2;

// One plotted quote.
interface CounterpartyQuotePoint {
  tenorDate: Date;
  quote: number;
  quoteBps?: number; // the plotted value on the Spread axis (LQC-2883)
  notation?: string;
  cpName: string;
  blockId: number;
  securityName?: string;
  quoteSide?: QuoteSide;
}

export interface CounterpartyQuoteSeriesInput {
  quotes: CounterpartyQuoteMarker[];
  palette: string[];
  // Where CP colours start in the overlay palette, so they don't collide with the indicative mid.
  paletteOffset: number;
  // The chart surface, used for the ring that separates a dot from whatever is behind it.
  surface: string;
  // Neutral colour for counterparties past the palette cap. They keep their own shape, legend
  // entry and tooltip, but share this rather than inventing an unvalidated hue.
  overflowColour: string;
  // Anchor for a quote whose leg carries no maturity - the earliest curve tenor. Quotes with no
  // maturity and no fallback are dropped, matching the indicative mid.
  fallbackTenor?: Date;
}

// Tooltip for every CP series: who quoted, on what, and at what level. The rate shares the title
// row with the name - it is what you compare between dots, so it sits on the headline rather than
// below the security. Both stay on one line; only the security (swap names are long) may wrap.
const counterpartyTooltipRenderer = (params: any) => {
  const datum = params.datum ?? {};
  const side = datum.quoteSide ? `${datum.quoteSide} ` : "";
  return `<div class="ag-chart-tooltip-title" style="display:flex;align-items:baseline;justify-content:space-between;gap:24px;white-space:nowrap;padding:6px 10px;"><span>${datum.cpName}</span><span>${side}${formatQuoteWithUnit(datum.quote, datum.notation)}</span></div><div class="ag-chart-tooltip-content" style="max-width:300px;padding:6px 10px;">${securityLabel(datum)}</div>`;
};

// Quotes turned into plotted points, anchoring any without a maturity to the fallback tenor.
// Quotes with no anchor at all are dropped.
const toPoints = (
  quotes: CounterpartyQuoteMarker[],
  fallbackTenor?: Date,
): CounterpartyQuotePoint[] =>
  quotes
    .flatMap((q) => {
      const tenorDate = q.tenorDate ?? fallbackTenor;
      if (!(tenorDate instanceof Date)) return [];
      return [
        {
          tenorDate,
          quote: q.quote,
          ...(yAxisForNotation(q.notation) === "spread" && {
            quoteBps: q.quote,
          }),
          notation: q.notation,
          cpName: q.cpName,
          blockId: q.blockId,
          securityName: q.securityName,
          quoteSide: q.quoteSide,
        },
      ];
    })
    .sort((a, b) => a.tenorDate.getTime() - b.tenorDate.getTime());

// The AG Charts series drawing the given CP quotes. Returns [] when there is nothing to draw, so
// the caller can spread the result unconditionally.
export const buildCounterpartyQuoteSeries = ({
  quotes,
  palette,
  paletteOffset,
  surface,
  overflowColour,
  fallbackTenor,
}: CounterpartyQuoteSeriesInput): any[] => {
  if (!quotes || quotes.length === 0) return [];

  const groups = rankCounterpartyGroups(groupQuotesByCounterparty(quotes));

  const seriesPerGroup = groups.map((group) => {
    // Colour and shape follow `cpIndex` - the CP's position in the trade's own CP list - NOT its
    // ranking and NOT its position among those that have quoted. A re-rank must not repaint the
    // chart, and neither must a CP quoting for the first time: keying off the quoting order
    // renumbered everyone already plotted the moment a second CP came in (LQC-1899).
    // Assigned in sequence and never cycled: past the palette's validated slots a counterparty
    // takes the neutral overflow colour rather than wrapping onto a colour already in use.
    const slot = paletteOffset + group.cpIndex;
    const colour = slot < palette.length ? palette[slot] : overflowColour;

    // One series per axis the CP quoted on (LQC-2883); the shared legendItemName keeps one legend
    // entry, so a click still toggles the whole counterparty.
    const axes: SwapCurveYAxis[] = ["rate", "spread"];
    return axes.flatMap((axis) => {
      const points = toPoints(
        group.quotes.filter((q) => yAxisForNotation(q.notation) === axis),
        fallbackTenor,
      );
      if (points.length === 0) return [];
      return [
        {
          type: "scatter",
          data: points,
          xKey: "tenorDate",
          yKey: axis === "spread" ? spreadYKey("quote") : "quote",
          yName: group.cpName,
          legendItemName: group.cpName,
          // Always explicit: AG Charts reuses series instances by type/xKey/yKey on update, and an
          // omitted key diffs to undefined, so the leader's old `false` would hide the next CP.
          showInLegend: true,
          tooltip: { renderer: counterpartyTooltipRenderer },
          stroke: colour,
          fill: colour,
          marker: {
            shape: CP_MARKER_SHAPES[group.cpIndex % CP_MARKER_SHAPES.length],
            size: CP_MARKER_SIZE,
            fill: colour,
            stroke: surface,
            strokeWidth: CP_MARKER_RING_WIDTH,
          },
        },
      ];
    });
  });

  // Legend follows quote rank (LQC-3190), but AG Charts draws later series on top and the leader
  // must win an overlap. So the leader's dots go last, hidden from the legend, and an empty proxy
  // holds its legend slot; the shared legendItemName still toggles both.
  const [leaderSeries, ...otherSeries] = seriesPerGroup;
  if (!leaderSeries?.length) return otherSeries.flat();
  const legendProxy = { ...leaderSeries[0], data: [] };
  return [
    legendProxy,
    ...otherSeries.flat(),
    ...leaderSeries.map((s) => ({ ...s, showInLegend: false })),
  ];
};
